import { PillKind } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';

import { Company } from './entities';
import { UnifiedCompanySignupStatus, UnifiedCompanyRelationshipSignupStatus } from '../lib/graphql';
import { beautifyString } from '../lib/util/string';
import { Routes } from '../routes';

type CompanyToCompare = Pick<Company, 'company_name'>;

export interface CompanyToursLink {
  label: string;
  href: string;
}

export function getCompanyToursLinks({
  companyId,
  isShipper,
  isCarrier,
}: {
  companyId: string;
  isShipper: boolean | null;
  isCarrier: boolean | null;
}): CompanyToursLink[] {
  const toursPath = Routes.Tours.generatePath({});
  const isShipperAndCarrier = (isShipper === true && isCarrier === true) || (!isShipper && !isCarrier);

  if (isShipperAndCarrier) {
    return [
      { label: 'Open Shipper Tours', href: `${toursPath}?shipper_id=${companyId}` },
      { label: 'Open Carrier Tours', href: `${toursPath}?carrier_id=${companyId}` },
    ];
  }

  if (isShipper === true) {
    return [{ label: 'Open Tours', href: `${toursPath}?shipper_id=${companyId}` }];
  }

  if (isCarrier === true) {
    return [{ label: 'Open Tours', href: `${toursPath}?carrier_id=${companyId}` }];
  }

  return [];
}

export const compareCompany = (c1: CompanyToCompare, c2: CompanyToCompare) => {
  return c1.company_name === c2.company_name || c1.company_name === null || c2.company_name === null
    ? 0
    : c1.company_name.localeCompare(c2.company_name);
};

export const mapIdentifierType = (identifierType: string): string => {
  switch (identifierType) {
    case 'VAT_NUMBER':
      return 'VAT';
    case 'USDOT_NUMBER':
      return 'DOT';
    case 'MC_NUMBER':
      return 'MC';
    case 'MISCELLANEOUS':
      return 'MISC ID';
    case 'TAX_IDENTIFICATION_NUMBER':
      return 'TIN';
    case 'TTC_SHARING_AGREEMENT_ID':
      return 'TTC ID';
    default:
      return identifierType;
  }
};

export const isCompanySignupStatusCompatibleForIntegration = (
  status: UnifiedCompanySignupStatus | null | undefined,
): boolean => {
  return status === UnifiedCompanySignupStatus.NO_VISIBILITY_SOURCE || status === UnifiedCompanySignupStatus.ONBOARDED;
};

export const getSignupStatusName = (
  type: UnifiedCompanySignupStatus | UnifiedCompanyRelationshipSignupStatus | null,
) => {
  if (type === null) {
    return 'Unknown';
  }

  switch (type) {
    case UnifiedCompanySignupStatus.NO_VISIBILITY_SOURCE:
    case UnifiedCompanyRelationshipSignupStatus.NO_VISIBILITY_SOURCE:
      return 'Visibility source pending';
    default:
      return beautifyString(type);
  }
};

// The Data Processing Addendum (DPA) became part of the standard Platform User
// Agreement starting with PUA v14 (March 1, 2024). Carriers on this version or
// later are considered DPA-compliant.
export const DPA_MINIMUM_PUA_VERSION = 14;

export interface CompanyAgreementSummary {
  hasAgreement: boolean;
  agreementVersion: number | null;
}

export function getAgreementDpaPill(agreement: CompanyAgreementSummary | null): { kind: PillKind; label: string } {
  // No PUA on record (no agreement record, or an explicit no-agreement record)
  // means the carrier is on a Light registration.
  if (isNil(agreement) || !agreement.hasAgreement) {
    return { kind: 'warning', label: 'Light registration' };
  }
  if (agreement.agreementVersion === null) {
    return { kind: 'neutral', label: 'PUA (version unknown)' };
  }
  if (agreement.agreementVersion >= DPA_MINIMUM_PUA_VERSION) {
    return { kind: 'success', label: `DPA · PUA v${agreement.agreementVersion}` };
  }
  return { kind: 'warning', label: `No DPA · PUA v${agreement.agreementVersion}` };
}

// Produces a numeric ordering for the "Agreement / DPA" column that mirrors the
// pill semantics: Light registration < PUA (version unknown) < PUA by ascending
// version (so DPA-compliant v14+ companies sort highest). A missing agreement
// record and an explicit no-agreement record both map to "Light registration",
// so they share the same sort value.
export function getAgreementSortValue(agreement: CompanyAgreementSummary | null): number {
  if (isNil(agreement) || !agreement.hasAgreement) {
    return 0;
  }
  if (agreement.agreementVersion === null) {
    return 1;
  }
  return 100 + agreement.agreementVersion;
}

export function getOnboardingStatusPillKind(
  status: UnifiedCompanySignupStatus | UnifiedCompanyRelationshipSignupStatus,
): PillKind {
  switch (status) {
    case UnifiedCompanySignupStatus.INVITED:
    case UnifiedCompanySignupStatus.REGISTERED:
      return 'warning';
    case UnifiedCompanySignupStatus.NO_VISIBILITY_SOURCE:
      return 'warning';
    case UnifiedCompanySignupStatus.ONBOARDED:
      return 'success';
    case UnifiedCompanyRelationshipSignupStatus.OUT_OF_SCOPE:
      return 'neutral';
    default:
      return 'neutral';
  }
}
